import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion, fetchConfig } from '@theme/utilities';

/**
 * Closes the storefront search drawer (`#search-modal` / `dialog-component`).
 * Exists on window for optional reuse (checkout, etc.).
 */
function closeThemeSearchDrawer() {
  const host = /** @type {(HTMLElement & { closeDialog?: () => unknown }) | null} */ (
    document.getElementById('search-modal')
  );
  if (!host) return;
  if (typeof host.closeDialog === 'function') {
    void host.closeDialog();
    return;
  }
  const dlg = host.querySelector('dialog');
  if (dlg instanceof HTMLDialogElement && dlg.open) {
    dlg.classList.remove('dialog-closing');
    dlg.close();
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.width = '';
  }
}

globalThis.__closeStorefrontSearchModal = closeThemeSearchDrawer;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();
    const baseHref = productLink?.href ||  this.dataset.productUrl || '';

    if (!baseHref) return '';

    const url = new URL(baseHref, window.location.origin);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // Get the button that was clicked
    const button = event.target.closest('.quick-add__button');
    if (button) {
      this.#setButtonLoading(button, true);
    }

    try {
      // Store current product handle
      const currentHandle = this.#getCurrentProductHandle();
      if (currentHandle) {
        this.dataset.currentProductHandle = currentHandle;
      }

      // Populate all product images (main + related)
      this.#populateProductImages();

      const currentUrl = this.productPageUrl;

      // Check if we have cached content for this URL
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
      }

      // IMPORTANT: Fetch sizes & inventory only AFTER modal content is built,
      // so the inputs exist when we try to apply inventory.
      if (currentHandle) {
        // Fetch the HTML again to extract garment lengths (if not cached)
        if (!this.#cachedContent.has(currentUrl)) {
          const html = await this.fetchProductPage(currentUrl);
          if (html) {
            await this.#extractGarmentLengthsFromHTML(html, currentHandle);
          }
        }
        await this.#fetchProductInventory(currentHandle);
      }

      this.#openQuickAddModal();
    } finally {
      // Remove loading state when drawer opens
      if (button) {
        this.#setButtonLoading(button, false);
      }
    }
  };

  /**
   * Populates all product images (main + related) in the SwatchColor container
  
   */
  #populateProductImages() {
    const imagesContainer = document.getElementById('quick-add-modal-images');
    if (!imagesContainer) return;

    const productHandles = this.dataset.productHandles || '';
    const productImages = this.dataset.productImages || '';
    const productColors = this.dataset.productColors || '';

    let imagesHtml = '';
    let currentHandle = '';
    let handlesArray = [];
    let colorsArray = [];

    if (productHandles && productHandles.trim() !== '') {
      handlesArray = productHandles.split(',').map(h => h.trim()).filter(h => h !== '');
      const imagesArray = productImages.split(',').map(i => i.trim());
      colorsArray = productColors.split(',').map(c => c.trim());

      currentHandle =
        this.dataset.currentProductHandle || this.#getCurrentProductHandle();

      handlesArray.forEach((handle, index) => {
        const imageUrl =
          imagesArray[index] || this.dataset.productImage || '';
        if (!handle || !imageUrl) return;

        const colorName = colorsArray[index] || '';
        const isSelected =
        !!currentHandle && handle === currentHandle
          ? ' selected'
          : '';
        imagesHtml += `
          <span class="insideDrawerItem">
            <span class="insideDrawerimg${isSelected}" ProductdataHandle="${handle}">
              <img src="${imageUrl}" alt="${colorName || handle}">
            </span>
            ${
              colorName
                ? `<span class="drawer-color-name">${colorName}</span>`
                : ''
            }
          </span>
        `;
      });
    } else {
      // Fallback: only main product image, no metafield list
      const mainImageUrl = this.dataset.productImage || '';
      currentHandle =
        this.dataset.currentProductHandle || this.#getCurrentProductHandle();

      if (mainImageUrl && currentHandle) {
        imagesHtml += `
          <span class="insideDrawerItem">
            <span class="insideDrawerimg selected" ProductdataHandle="${currentHandle}">
              <img src="${mainImageUrl}" alt="Product image">
            </span>
          </span>
        `;
      }
    }

    imagesContainer.innerHTML = imagesHtml;

    // Update color heading with selected color
    if (productHandles && productHandles.trim() !== '') {
      this.#updateColorHeading(currentHandle, handlesArray, colorsArray);
    } else {
      // Fallback: keep label visible even without color data
      const colorHeading = document.getElementById('quick-add-modal-color-heading');
      if (colorHeading) {
        colorHeading.innerHTML = '<span class="color-label">Color: </span><span class="color-value"></span>';
      }
    }

    // Enable mouse drag scrolling first
    const dragState = this.#enableDragScroll(imagesContainer);

    // Add click handlers to all images (prevent click if drag occurred)
    const allImages = imagesContainer.querySelectorAll('.insideDrawerimg');
    allImages.forEach(img => {
      img.addEventListener('click', (e) => {
        // Don't trigger click if user was dragging
        if (dragState.hasDragged) {
          e.preventDefault();
          e.stopPropagation();
          dragState.hasDragged = false;
          return;
        }
        const handle = img.getAttribute('ProductdataHandle');
        if (handle) {
          // Get color name from the clicked image's parent item
          const colorName = img.closest('.insideDrawerItem')?.querySelector('.drawer-color-name')?.textContent || '';
          this.__fetchProductData(handle, img, colorName);
        }
      });
    });
  }

  /**
   * Updates the color heading with the selected color
   * @param {string} selectedHandle - The currently selected product handle
   * @param {string[]} handlesArray - Array of all product handles
   * @param {string[]} colorsArray - Array of all color names
   */
  #updateColorHeading(selectedHandle, handlesArray, colorsArray) {
    const colorHeading = document.getElementById('quick-add-modal-color-heading');
    if (!colorHeading) return;

    if (!selectedHandle || !Array.isArray(handlesArray) || !Array.isArray(colorsArray) || colorsArray.length === 0) {
      colorHeading.innerHTML = '<span class="color-label">Color: </span><span class="color-value"></span>';
      return;
    }

    const selectedIndex = handlesArray.indexOf(selectedHandle);
    const selectedColor = selectedIndex !== -1 ? (colorsArray[selectedIndex] || '').trim() : '';

    if (selectedColor) {
      colorHeading.innerHTML = '<span class="color-label">Color: </span><span class="color-value">' + selectedColor + '</span>';
    } else {
      colorHeading.innerHTML = '<span class="color-label">Color: </span><span class="color-value"></span>';
    }
  }

  /**
   * Enables mouse drag scrolling for the images container
   * @param {HTMLElement} container - The container element to enable drag scrolling on
   * @returns {{hasDragged: boolean}} - State object to track if dragging occurred
   */
  #enableDragScroll(container) {
    if (!container) return { hasDragged: false };

    const dragState = { hasDragged: false };
    let isDown = false;
    let startX;
    let scrollLeft;
    let startScrollLeft;

    container.addEventListener('mousedown', (e) => {
      isDown = true;
      dragState.hasDragged = false;
      container.style.cursor = 'grabbing';
      startX = e.pageX - container.offsetLeft;
      scrollLeft = container.scrollLeft;
      startScrollLeft = scrollLeft;
    });

    container.addEventListener('mouseleave', () => {
      isDown = false;
      container.style.cursor = 'grab';
    });

    container.addEventListener('mouseup', () => {
      isDown = false;
      container.style.cursor = 'grab';
      // Reset after a short delay to allow click event to check
      setTimeout(() => {
        dragState.hasDragged = false;
      }, 100);
    });

    container.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 2; // Scroll speed multiplier
      const newScrollLeft = startScrollLeft - walk;
      
      // Check if scroll position actually changed (user dragged)
      if (Math.abs(newScrollLeft - startScrollLeft) > 3) {
        dragState.hasDragged = true;
      }
      
      container.scrollLeft = newScrollLeft;
    });

    return dragState;
  }

  /**
   * Gets the current product handle from the product page URL.
   * @returns {string} Product handle
   */
  #getCurrentProductHandle() {
    const productUrl = this.productPageUrl;
    if (!productUrl) {
      // Predictive-search/resource-card fallback
      const handles = (this.dataset.productHandles || '').split(',').map((h) => h.trim()).filter(Boolean);
      return handles[0] || '';
    }
    
    try {
      const url = new URL(productUrl, window.location.origin);
      const pathParts = url.pathname.split('/');
      const handleIndex = pathParts.indexOf('products');
      if (handleIndex !== -1 && pathParts[handleIndex + 1]) {
        let handle = pathParts[handleIndex + 1] || '';
        if (!handle) return '';
        // Remove query params if any
        if (handle.includes('?')) {
          handle = handle.split('?')[0];
        }
        return handle;
      }
    } catch (e) {
      console.warn('Could not extract product handle from URL');
    }
    return '';
  }

  /**
   * Fetches product data for the clicked product handle

   * Public method accessible from onclick handlers
   * @param {string} productHandle - Product handle to fetch
   * @param {Element} clickedElement - The clicked image element
   * @param {string} colorName - Optional color name for the clicked product
   */
  async __fetchProductData(productHandle, clickedElement, colorName = '') {
    if (!productHandle) return;

    try {
      // Update selected state
      const imagesContainer = document.getElementById('quick-add-modal-images');
      if (imagesContainer) {
        const allImages = imagesContainer.querySelectorAll('.insideDrawerimg');
        allImages.forEach(img => img.classList.remove('selected'));
        if (clickedElement) {
          clickedElement.classList.add('selected');
        }
      }

      // Update color heading if color name is provided
      if (colorName) {
        const colorHeading = document.getElementById('quick-add-modal-color-heading');
        if (colorHeading) {
          colorHeading.innerHTML = '<span class="color-label">Color: </span><span class="color-value">' + colorName + '</span>';
        }
      } else {
        // Try to get color from the clicked element's parent
        const colorElement = clickedElement?.closest('.insideDrawerItem')?.querySelector('.drawer-color-name');
        if (colorElement) {
          const colorHeading = document.getElementById('quick-add-modal-color-heading');
          if (colorHeading) {
            colorHeading.innerHTML = '<span class="color-label">Color: </span><span class="color-value">' + colorElement.textContent + '</span>';
          }
        } else {
          const colorHeading = document.getElementById('quick-add-modal-color-heading');
          if (colorHeading) {
            colorHeading.innerHTML = '<span class="color-label">Color: </span><span class="color-value"></span>';
          }
        }
      }

      // Remember current handle before any downstream updates
      this.dataset.currentProductHandle = productHandle;

      // Fetch the product page HTML for this handle and rebuild the
      // modal content so that the internal product-form-component and
      // variant picker now belong to the clicked product.
      const productUrl = `/products/${productHandle}`;
      const html = await this.fetchProductPage(productUrl);

      if (html) {
        const productGrid = html.querySelector('[data-product-grid-content]');
        if (productGrid) {
          const freshGrid = /** @type {Element} */ (productGrid.cloneNode(true));
          await this.updateQuickAddModal(freshGrid);
          
          // Clear size selections when variant changes
          this.#clearSizeSelection();
          
          // Try to extract garment lengths from the HTML
          await this.#extractGarmentLengthsFromHTMLForProduct(html, productHandle);
          
          // Update headings after modal content is updated
          const modalContent = document.getElementById('quick-add-modal-content');
          if (modalContent) {
            setTimeout(() => {
              this.#updateHeadingsFromVariant(modalContent);
            }, 150);
          }
        } else {
          console.warn(
            '[QuickAdd] Could not find product grid for handle:',
            productHandle
          );
        }
      } else {
        console.warn('[QuickAdd] Could not fetch product page for', productHandle);
      }

      // Refresh sizes & inventory for the newly selected product
      // Wait a bit longer to ensure morph has completed and DOM is ready
      // Also ensure the size wrapper exists before fetching
      setTimeout(async () => {
        const sizeWrapper = document.querySelector('.SizeVariantsWrapper');
        if (sizeWrapper) {
          // Clear any existing size variants before fetching new ones
          sizeWrapper.innerHTML = '';
          await this.#fetchProductInventory(productHandle);
        } else {
          console.warn('[QuickAdd] SizeVariantsWrapper not found, retrying inventory fetch...');
          // Retry after another delay
          setTimeout(async () => {
            const retryWrapper = document.querySelector('.SizeVariantsWrapper');
            if (retryWrapper) {
              retryWrapper.innerHTML = '';
              await this.#fetchProductInventory(productHandle);
            } else {
              console.error('[QuickAdd] SizeVariantsWrapper still not found after retry');
            }
          }, 200);
        }
      }, 300);
    } catch (error) {
      console.error(`Error fetching product data for ${productHandle}:`, error);
    }
  }

  /**
   * Fetch product variants (sizes) and inventory via Storefront GraphQL
   * and apply to the quick view modal.
   * @param {string} productHandle
   */
  async #fetchProductInventory(productHandle) {
    if (!productHandle) return;

    try {
      // Ensure current handle reflects the one we're fetching for
      this.dataset.currentProductHandle = productHandle;

      const graphqlQuery = {
        query: `
          query GetProductInventory($handle: String!) {
            productByHandle(handle: $handle) {
              id
              metafields(identifiers: [
                {namespace: "custom", key: "size_chart"}
              ]) {
                id
                namespace
                key
                value
                reference {
                  ... on Metaobject {
                    id
                    fields {
                      key
                      value
                    }
                  }
                }
              }
              variants(first: 50) {
                nodes {
                  id
                  availableForSale
                  quantityAvailable
                  title
                  selectedOptions {
                    name
                    value
                  }
                }
              }
            }
          }
        `,
        variables: { handle: productHandle },
      };

      const storefrontEndpoint =
        'https://styleup-store-india.myshopify.com/api/2024-04/graphql.json';

      const response = await fetch(storefrontEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': '5c16499cd722e77c804742c06133aabd',
        },
        body: JSON.stringify(graphqlQuery),
      });

      if (!response.ok) return;

      const result = await response.json();
      const product = result?.data?.productByHandle;
      const variants = product?.variants?.nodes || [];
      
      console.log('[QuickAdd] Fetched inventory for product:', productHandle, 'Variants:', variants.length);
      
      // Extract product ID from GraphQL ID (format: gid://shopify/Product/123456789)
      if (product?.id) {
        const productIdMatch = product.id.match(/\/Product\/(\d+)/);
        if (productIdMatch && productIdMatch[1]) {
          this.dataset.currentProductId = productIdMatch[1];
        }
      }

      // Extract and store garment lengths data
      this.#extractAndStoreGarmentLengths(product);

      // Apply inventory to modal - this will update size variants and disable ATC if all sold out
      this.#applyInventoryToModal(variants);
    } catch (error) {
      console.error('Error fetching product inventory:', error);
    }
  }

  /**
   * Render simple size buttons inside .SizeVariantsWrapper
   * using the inventory data from GraphQL.
   * @param {{id:string, availableForSale:boolean, quantityAvailable:number, title:string, selectedOptions:{name:string,value:string}[]}[]} variants
   */
  #applyInventoryToModal(variants) {
    if (!variants || !Array.isArray(variants) || variants.length === 0) {
      console.warn('[QuickAdd] No variants to apply to modal', variants);
      // Clear size wrapper if no variants
      const sizeWrapper = document.querySelector('.SizeVariantsWrapper');
      if (sizeWrapper) {
        sizeWrapper.innerHTML = '';
      }
      // Hide entire size section if no variants
      const sizeSwatchWrapper = document.querySelector('.SizeSwatchWrapper');
      if (sizeSwatchWrapper instanceof HTMLElement) {
        sizeSwatchWrapper.style.display = 'none';
        const oosNote = sizeSwatchWrapper.querySelector('[data-all-sizes-oos]');
        if (oosNote instanceof HTMLElement) {
          oosNote.hidden = true;
        }
      }
      // Disable ATC buttons if no variants
      const dialog = document.getElementById('quick-add-dialog');
      if (dialog) {
        const atcButton = dialog.querySelector('.product-form__submit') || dialog.querySelector('button[name="add"]');
        const footerAtc = dialog.querySelector('.quick-add-atc-button');
        if (atcButton instanceof HTMLButtonElement) {
          atcButton.disabled = true;
        }
        if (footerAtc instanceof HTMLButtonElement) {
          footerAtc.disabled = true;
        }
      }
      return;
    }

    const modalContent = document.getElementById('quick-add-modal-content');
    const sizeWrapper = document.querySelector('.SizeVariantsWrapper');
    
    if (!sizeWrapper) {
      console.warn('[QuickAdd] SizeVariantsWrapper not found in DOM');
      return;
    }
    
    if (!modalContent) {
      console.warn('[QuickAdd] quick-add-modal-content not found in DOM');
      return;
    }

    // Clear existing size variants
    sizeWrapper.innerHTML = '';

    let allInputsDisabled = true;
    let availableCount = 0;
    /** @type {HTMLInputElement | null} */
    let firstAvailableInput = null;

    variants.forEach((variant) => {
      if (!variant?.id) return;

      const parts = variant.id.split('/ProductVariant/');
      const shortId = parts[1] || variant.id;

      const qty = Number(variant.quantityAvailable ?? 0);
      const available = !!variant.availableForSale && qty > 0;

      const sizeLabel =
        (variant.selectedOptions && variant.selectedOptions[0]?.value) ||
        variant.title ||
        '';

      const disabledAttr = available ? '' : 'disabled';

      const sizeHtml = `
        <fieldset class="VariantbuttonGroup">
          <legend class="sr-only" style="display:none;">Size</legend>
          <div class="Variantbutton">
            <input
              type="radio"
              id="size-${shortId}"
              name="quick-add-size"
              value="${sizeLabel}"
              data-variant-id="${shortId}"
              data-quantityAv="${qty}"
              ${disabledAttr}
            />
            <label class="btn--Variant" for="size-${shortId}">${sizeLabel}</label>
          </div>
        </fieldset>
      `;

      sizeWrapper.insertAdjacentHTML('beforeend', sizeHtml);

      if (available) {
        allInputsDisabled = false;
        availableCount++;
        if (!firstAvailableInput) {
          firstAvailableInput =
            sizeWrapper.querySelector(
              `input[id="size-${shortId}"]`
            ) || null;
        }
      }
    });

    console.log('[QuickAdd] Applied inventory:', {
      totalVariants: variants.length,
      availableCount: availableCount,
      allSoldOut: allInputsDisabled
    });

    // Keep size pills visible when all are OOS (disabled) so shoppers see size range; ATC stays off.
    const sizeSwatchWrapper = document.querySelector('.SizeSwatchWrapper');
    if (sizeSwatchWrapper instanceof HTMLElement) {
      sizeSwatchWrapper.style.display = '';
      const oosNote = sizeSwatchWrapper.querySelector('[data-all-sizes-oos]');
      if (oosNote instanceof HTMLElement) {
        oosNote.hidden = !allInputsDisabled;
      }
    }

    const dialog = document.getElementById('quick-add-dialog');
    if (!dialog) return;

    const atcButton =
      dialog.querySelector('.product-form__submit') ||
      dialog.querySelector('button[name="add"]');

    // Hidden variant input used by the theme's add-to-cart form
    const variantIdInput = dialog.querySelector('input[name="id"]');

    // Attach click handlers to new size radios
    const sizeInputs = sizeWrapper.querySelectorAll(
      'input[type="radio"][name="quick-add-size"]'
    );

    sizeInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (!(input instanceof HTMLInputElement)) return;
        const variantId = input.dataset.variantId;
        if (!variantId) return;

        // Hide error message when size is selected
        const errorMsg = document.querySelector('[data-size-error]');
        if (errorMsg) {
          errorMsg.classList.remove('show');
        }

        // Update size heading immediately
        this.#updateSizeHeading(input);

        // Update garment lengths for selected size
        this.#updateGarmentLengths(input.value);

        // Enable add to cart buttons if they were disabled (only needed if all were sold out)
        // Buttons are already enabled by default (unless all sizes are sold out)
        if (!input.disabled) {
          if (atcButton instanceof HTMLButtonElement && atcButton.disabled) {
            atcButton.disabled = false;
          }
          const footerAtc = dialog.querySelector('.quick-add-atc-button');
          if (footerAtc instanceof HTMLButtonElement && footerAtc.disabled) {
            footerAtc.disabled = false;
          }
        }

        // Sync with the theme's built-in variant picker by "clicking"
        // the matching input that has data-variant-id (gid suffix match).
        const themeVariantInput = modalContent.querySelector(
          `input[type="radio"][data-variant-id$="${variantId}"]`
        );

        if (themeVariantInput instanceof HTMLInputElement) {
          if (!themeVariantInput.checked) {
            themeVariantInput.checked = true;
          }
          themeVariantInput.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        } else if (variantIdInput instanceof HTMLInputElement) {
          // Fallback for products where the quick-add variant picker
          // doesn't have this variant (e.g., related products):
          // write the numeric id directly so ATC adds the correct variant.
          variantIdInput.value = variantId;
        }
      });
    });

    // Do not select any size by default - user must select manually
    // Removed default selection to match complete-the-look behavior
    
    // Clear all size selections and show error message
    this.#clearSizeSelection();

    if (atcButton instanceof HTMLButtonElement) {
      // Only disable if all inputs are disabled (sold out)
      // Keep enabled so user can click and see error message if no size selected
      atcButton.disabled = allInputsDisabled;
    }

    // Also control footer ATC button and View Product link
    const footerAtc = dialog.querySelector(
      '.quick-add-atc-button'
    );
    const viewLink = dialog.querySelector(
      '.quick-add-view-product'
    );

    if (footerAtc instanceof HTMLButtonElement) {
      // Only disable if all inputs are disabled (sold out)
      // Keep enabled so user can click and see error message if no size selected
      footerAtc.disabled = allInputsDisabled;

      // Remove any existing onclick handler
      footerAtc.onclick = null;

      // Remove old listener if it exists (using named function for easier removal)
      if (footerAtc._atcClickHandler) {
        footerAtc.removeEventListener('click', footerAtc._atcClickHandler);
      }

      // Create named handler function for better mobile support
      // Find the form when the button is clicked (in case it's not ready yet)
      if (footerAtc._atcTouchHandler) {
        footerAtc.removeEventListener('touchend', footerAtc._atcTouchHandler);
        footerAtc._atcTouchHandler = null;
      }

      footerAtc._atcClickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();

        /** Close predictive search drawer if it stayed open behind quick-add (click + programmatic touchend path). */
        closeThemeSearchDrawer();

        // Ensure a size is selected
        const selectedSize = sizeWrapper.querySelector(
          'input[type="radio"][name="quick-add-size"]:checked'
        );

        if (!selectedSize) {
          // Show error message
          const errorMsg = document.querySelector('[data-size-error]');
          if (errorMsg) {
            errorMsg.classList.add('show');
          }
          return;
        }

        // Hide error message if size is selected
        const errorMsg = document.querySelector('[data-size-error]');
        if (errorMsg) {
          errorMsg.classList.remove('show');
        }

        const selectedVariantId = selectedSize.dataset.variantId;
        if (selectedVariantId) {
          const themeVariantInput = modalContent?.querySelector(
            `input[type="radio"][data-variant-id$="${selectedVariantId}"]`
          );

          if (themeVariantInput instanceof HTMLInputElement) {
            themeVariantInput.checked = true;
            themeVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          const hiddenIdInput =
            dialog.querySelector('input[name="id"]') ||
            modalContent?.querySelector('input[name="id"]');
          if (hiddenIdInput instanceof HTMLInputElement) {
            hiddenIdInput.value = selectedVariantId;
          }
        }

        // Try to find the product form - try multiple selectors and locations
        let productForm = null;
        const selectors = [
          'product-form-component form',
          'form[action*="/cart/add"]',
          'form[action*="/cart"]',
          'form',
          'product-form form'
        ];

        const searchLocations = [
          dialog,
          modalContent,
          document.querySelector('#quick-add-modal-content'),
          document.querySelector('quick-add-dialog')
        ];

        for (const location of searchLocations) {
          if (!location) continue;
          for (const selector of selectors) {
            productForm = location.querySelector(selector);
            if (productForm instanceof HTMLFormElement) {
              break;
            }
          }
          if (productForm) break;
        }

        if (!(productForm instanceof HTMLFormElement)) {
          // Fallback: Submit directly to cart API using the variant ID
          const variantId = selectedSize.dataset.variantId;

          if (variantId) {
            this.#setButtonLoading(footerAtc, true);

            // Create FormData for cart submission
            const formData = new FormData();
            formData.append('id', variantId);
            formData.append('quantity', '1');

            // Add product-id if available (required for some cart operations)
            const productId = this.dataset.currentProductId;
            if (productId) {
              formData.append('product-id', productId);
            }

            // Get cart items components for sections
            const cartItemsComponents = document.querySelectorAll('cart-items-component');
            const cartItemComponentsSectionIds = [];
            cartItemsComponents.forEach((item) => {
              if (item instanceof HTMLElement && item.dataset.sectionId) {
                cartItemComponentsSectionIds.push(item.dataset.sectionId);
              }
            });
            if (cartItemComponentsSectionIds.length > 0) {
              formData.append('sections', cartItemComponentsSectionIds.join(','));
            }

            const removeLoading = () => {
              this.#setButtonLoading(footerAtc, false);
              document.removeEventListener(CartAddEvent.eventName, handleCartAdd);
              document.removeEventListener(CartUpdateEvent.eventName, handleCartUpdate);
              document.removeEventListener(CartErrorEvent.eventName, handleCartError);
            };

            const handleCartSuccess = () => {
              removeLoading();
              this.#closeQuickAddModal();
            };

            const handleCartAdd = handleCartSuccess;
            const handleCartUpdate = handleCartSuccess;
            const handleCartError = () => removeLoading();

            document.addEventListener(CartAddEvent.eventName, handleCartAdd, { once: true });
            document.addEventListener(CartUpdateEvent.eventName, handleCartUpdate, { once: true });
            document.addEventListener(CartErrorEvent.eventName, handleCartError, { once: true });

            // Use theme's fetchConfig
            const fetchCfg = fetchConfig('javascript', { body: formData });

            // Get cart add URL from theme routes
            const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';

            fetch(cartAddUrl, fetchCfg)
              .then((response) => {
                if (response.ok) {
                  return response.json();
                }
                throw new Error(`Cart API error: ${response.status}`);
              })
              .then((data) => {
                if (data.status === 'error' || data.status === 'unprocessable_entity') {
                  document.dispatchEvent(
                    new CartErrorEvent('quick-add', data.message || 'Error adding to cart', data.description || {}, data.errors || {})
                  );
                } else {
                  this.#closeQuickAddModal();
                  document.dispatchEvent(new CartAddEvent(data, 'quick-add', { variantId }));
                  document.dispatchEvent(new CartUpdateEvent(data, 'quick-add', { variantId }));
                }
              })
              .catch(() => {
                document.dispatchEvent(
                  new CartErrorEvent('quick-add', 'Unable to add to cart. Please try again.', {}, {})
                );
              });

            return;
          }

          alert('Unable to add to cart. Please try again.');
          return;
        }

        const idInput = productForm.querySelector('input[name="id"]');
        if (idInput instanceof HTMLInputElement && selectedVariantId) {
          idInput.value = selectedVariantId;
          idInput.disabled = false;
        }

        // Always add one unit — do not reuse cart line qty for another variant
        const quantityInput = productForm.querySelector('input[name="quantity"]');
        if (quantityInput instanceof HTMLInputElement) {
          quantityInput.value = '1';
        }

        // Set loading state
        this.#setButtonLoading(footerAtc, true);

        // Listen for cart events to remove loading state
        const removeLoading = () => {
          this.#setButtonLoading(footerAtc, false);
          document.removeEventListener(CartAddEvent.eventName, handleCartAdd);
          document.removeEventListener(CartUpdateEvent.eventName, handleCartUpdate);
          document.removeEventListener(CartErrorEvent.eventName, handleCartError);
        };

        const handleCartSuccess = () => {
          removeLoading();
          this.#closeQuickAddModal();
        };

        const handleCartAdd = handleCartSuccess;
        const handleCartUpdate = handleCartSuccess;
        const handleCartError = () => removeLoading();

        document.addEventListener(CartAddEvent.eventName, handleCartAdd, { once: true });
        document.addEventListener(CartUpdateEvent.eventName, handleCartUpdate, { once: true });
        document.addEventListener(CartErrorEvent.eventName, handleCartError, { once: true });

        productForm.requestSubmit();
      };

      // Use addEventListener instead of onclick for better mobile support
      footerAtc.addEventListener('click', footerAtc._atcClickHandler, { passive: false });

      footerAtc._atcTouchHandler = (e) => {
        // Trigger click on touchend for mobile
        e.preventDefault();
        e.stopPropagation();
        footerAtc._atcClickHandler(e);
      };

      footerAtc.addEventListener('touchend', footerAtc._atcTouchHandler, { passive: false });
    }

    if (viewLink instanceof HTMLAnchorElement) {
      const handle =
        this.dataset.currentProductHandle || this.#getCurrentProductHandle();
      if (handle) {
        viewLink.href = `/products/${handle}`;
      }
    }
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  /**
   * Sets the loading state of a button
   * @param {HTMLElement} button - The button element
   * @param {boolean} isLoading - Whether the button should be in loading state
   */
  #setButtonLoading(button, isLoading) {
    // Avoid unnecessary DOM churn if state is unchanged
    if (button.classList.contains('is-loading') === isLoading) return;

    if (isLoading) {
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    } else {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    // Remove loading state from all quick-add buttons when drawer opens
    const buttons = document.querySelectorAll('.quick-add__button.is-loading');
    buttons.forEach(button => {
      this.#setButtonLoading(button, false);
    });

    // Clear all size selections when modal opens
    this.#clearSizeSelection();

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker and product details inside the quick-add modal.
   * @param {Element} productGrid - The product grid element from the product page
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    
    // Setup listeners after a short delay to ensure DOM is ready after morphing
    setTimeout(() => {
      this.#setupVariantChangeListeners(modalContent);
    }, 100);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  /**
   * Sets up listeners for variant changes to update color and size headings
   * @param {Element} modalContent - The modal content element
   */
  #setupVariantChangeListeners(modalContent) {
    // Listen for variant changes from the variant picker
    const variantPicker = modalContent.querySelector('variant-picker');
    if (variantPicker) {
      // Listen to change events on variant inputs
      const variantInputs = variantPicker.querySelectorAll('input[type="radio"][data-variant-id]');
      variantInputs.forEach(input => {
        input.addEventListener('change', () => {
          this.#updateHeadingsFromVariant(modalContent);
        });
      });
    }

    // Listen for size button changes
    const sizeWrapper = document.querySelector('.SizeVariantsWrapper');
    if (sizeWrapper) {
      const sizeInputs = sizeWrapper.querySelectorAll('input[type="radio"][name="quick-add-size"]');
      sizeInputs.forEach(input => {
        input.addEventListener('change', () => {
          this.#updateSizeHeading(input);
          // Also update color heading when size changes (variant might have different color)
          this.#updateHeadingsFromVariant(modalContent);
        });
      });
    }

    // Listen for variant update events from the theme
    const dialog = document.getElementById('quick-add-dialog');
    if (dialog) {
      const handleVariantUpdate = () => {
        this.#updateHeadingsFromVariant(modalContent);
      };
      dialog.addEventListener(ThemeEvents.variantUpdate, handleVariantUpdate);
    }

    // Initial update
    this.#updateHeadingsFromVariant(modalContent);
  }

  /**
   * Updates color and size headings based on the currently selected variant
   * @param {Element} modalContent - The modal content element
   */
  #updateHeadingsFromVariant(modalContent) {
    // Find the variant option labels to extract color and size
    const variantPicker = modalContent.querySelector('variant-picker');
    if (!variantPicker) return;

    // Get all variant option groups
    const variantGroups = variantPicker.querySelectorAll('[data-variant-group]');
    let colorValue = '';
    let sizeValue = '';
    const colorHeading = document.getElementById('quick-add-modal-color-heading');

    variantGroups.forEach(group => {
      const selectedInput = group.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
      if (!selectedInput) return;

      // Try multiple ways to get the label
      let label = group.querySelector('label[for="' + selectedInput.id + '"]');
      if (!label) {
        // Try to find label that contains the input
        label = selectedInput.closest('label');
      }
      if (!label) {
        // Try to get text from button label
        const buttonLabel = group.querySelector('.variant-option__button-label');
        if (buttonLabel) {
          label = buttonLabel;
        }
      }
      if (!label) return;

      const groupLabel = group.querySelector('.variant-option__label');
      const optionName = groupLabel?.textContent?.trim() || '';
      
      // Also check the fieldset legend or any label element
      const legend = group.querySelector('legend');
      const legendText = legend?.textContent?.trim() || '';
      const finalOptionName = optionName || legendText;

      // Get the option value - try multiple methods
      let optionValue = '';
      if (label) {
        // Remove icon/text spans and get clean text
        const labelClone = label.cloneNode(true);
        const icons = labelClone.querySelectorAll('.svg-wrapper, svg, .variant-option__icon');
        icons.forEach(icon => icon.remove());
        optionValue = labelClone.textContent?.trim() || '';
      }

      if (!optionValue) return;

      // Check if this is a color option (case-insensitive, check common variations)
      const optionNameLower = finalOptionName.toLowerCase();
      if (optionNameLower.includes('color') || 
          optionNameLower.includes('colour') ||
          optionNameLower.includes('couleur')) {
        colorValue = optionValue;
      }

      // Check if this is a size option (case-insensitive)
      if (optionNameLower.includes('size')) {
        sizeValue = optionValue;
      }
    });

    // Update color heading (keep label visible even without color)
    if (colorHeading) {
      if (colorValue) {
        colorHeading.innerHTML = '<span class="color-label">Color: </span><span class="color-value">' + colorValue + '</span>';
      } else if (!colorHeading.querySelector('.color-label')) {
        // Ensure label exists but don't clear an existing selection
        colorHeading.innerHTML = '<span class="color-label">Color: </span><span class="color-value"></span>';
      }
    }

    // Update size heading only if we found a size value AND a size is actually selected
    // Don't auto-update from variant picker if no size radio is checked
    if (sizeValue) {
      const sizeWrapper = document.querySelector('.SizeVariantsWrapper');
      const hasSelectedSize = sizeWrapper && sizeWrapper.querySelector('input[type="radio"][name="quick-add-size"]:checked');
      // Only update if a size radio button is actually checked by user
      if (hasSelectedSize) {
        this.#updateSizeHeadingFromValue(sizeValue);
      }
    }
  }
  

  /**
   * Updates the size heading from a size input element
   * @param {HTMLInputElement} sizeInput - The selected size input
   */
  #updateSizeHeading(sizeInput) {
    if (!sizeInput) return;
    const sizeValue = sizeInput.value || sizeInput.dataset.variantId || '';
    if (sizeValue) {
      this.#updateSizeHeadingFromValue(sizeValue);
    }
  }

  /**
   * Updates the size heading with a size value
   * @param {string} sizeValue - The size value to display (empty string to clear)
   */
  #updateSizeHeadingFromValue(sizeValue) {
    const selectedValueSpan = document.querySelector('[data-size-selected-value]');
    if (selectedValueSpan) {
      if (sizeValue && sizeValue.trim() !== '') {
        selectedValueSpan.textContent = sizeValue;
        selectedValueSpan.style.fontWeight = '500';
        selectedValueSpan.style.color = '#321E5A';
      } else {
        selectedValueSpan.textContent = '';
      }
    }
  }

  /**
   * Clears all size selections (without showing error message by default)
   */
  #clearSizeSelection() {
    // Clear all checked radio buttons
    const sizeWrapper = document.querySelector('.SizeVariantsWrapper');
    if (sizeWrapper) {
      const sizeInputs = sizeWrapper.querySelectorAll('input[type="radio"][name="quick-add-size"]');
      sizeInputs.forEach(input => {
        if (input instanceof HTMLInputElement) {
          input.checked = false;
        }
      });
    }

    // Clear size heading display
    const selectedValueSpan = document.querySelector('[data-size-selected-value]');
    if (selectedValueSpan) {
      selectedValueSpan.textContent = '';
    }

    // Hide error message (don't show by default)
    const errorMsg = document.querySelector('[data-size-error]');
    if (errorMsg) {
      errorMsg.classList.remove('show');
    }

    // Hide garment lengths
    const garmentLengthsOutput = document.querySelector('[data-garment-lengths-output]');
    if (garmentLengthsOutput) {
      garmentLengthsOutput.style.display = 'none';
    }
  }

  /**
   * Extracts garment lengths data from GraphQL response and stores it
   * @param {Object} product - The product object from GraphQL response
   */
  #extractAndStoreGarmentLengths(product) {
    const garmentLengthsOutput = document.querySelector('[data-garment-lengths-output]');
    if (!garmentLengthsOutput) {
      console.warn('[QuickAdd] Garment lengths output element not found');
      return;
    }

    // Try to get from GraphQL metafields first
    if (product?.metafields && product.metafields.length > 0) {
      // Find the size_chart metafield
      // Filter out null/undefined metafields before searching
      const sizeChartMetafield = product.metafields
        .filter(mf => mf != null)
        .find(
          mf => mf.namespace === 'custom' && mf.key === 'size_chart'
        );

      if (sizeChartMetafield?.reference) {
        // Get garment_lengths from the metaobject reference
        const metaobject = sizeChartMetafield.reference;
        const garmentLengthsField = metaobject.fields?.find(
          field => field.key === 'garment_lengths' || field.key === 'Garment Lengths'
        );

        if (garmentLengthsField?.value) {
          // Parse the garment lengths value (should be JSON string)
          let garmentLengthsData = '';
          try {
            // If it's already a string, parse it; if it's an object, stringify it
            if (typeof garmentLengthsField.value === 'string') {
              // Try to parse to validate it's valid JSON
              JSON.parse(garmentLengthsField.value);
              garmentLengthsData = garmentLengthsField.value;
            } else {
              garmentLengthsData = JSON.stringify(garmentLengthsField.value);
            }
            
            if (garmentLengthsData) {
              garmentLengthsOutput.setAttribute('data-garment-lengths-data', garmentLengthsData.replace(/"/g, '&quot;'));
              console.log('[QuickAdd] Garment lengths data stored from GraphQL');
              return;
            }
          } catch (e) {
            console.warn('[QuickAdd] Garment lengths data is not valid JSON:', e);
          }
        }
      }
    }

    // Fallback: Try to extract from product page HTML
    this.#extractGarmentLengthsFromHTML();
  }

  /**
   * Extracts garment lengths from the product page HTML (fallback method)
   * @param {Document} html - Optional HTML document to extract from
   * @param {string} productHandle - Optional product handle to fetch if html not provided
   */
  async #extractGarmentLengthsFromHTML(html = null, productHandle = null) {
    if (!html) {
      productHandle = productHandle || this.dataset.currentProductHandle || this.#getCurrentProductHandle();
      if (!productHandle) return;

      try {
        const productUrl = `/products/${productHandle}`;
        html = await this.fetchProductPage(productUrl);
        if (!html) return;
      } catch (error) {
        console.warn('[QuickAdd] Error fetching product page for garment lengths:', error);
        return;
      }
    }

    const garmentLengthsOutput = document.querySelector('[data-garment-lengths-output]');
    if (!garmentLengthsOutput) return;

    try {
      // Look for a script tag with garment lengths data
      const garmentLengthsScript = html.querySelector('script[data-garment-lengths]');
      if (garmentLengthsScript) {
        const garmentLengthsData = garmentLengthsScript.getAttribute('data-garment-lengths');
        if (garmentLengthsData) {
          garmentLengthsOutput.setAttribute('data-garment-lengths-data', garmentLengthsData.replace(/"/g, '&quot;'));
          console.log('[QuickAdd] Garment lengths data stored from script tag');
          return;
        }
      }

      // Try to find in variant picker data attribute
      const variantPicker = html.querySelector('variant-picker[data-garment-lengths]');
      if (variantPicker) {
        const garmentLengthsData = variantPicker.getAttribute('data-garment-lengths');
        if (garmentLengthsData) {
          garmentLengthsOutput.setAttribute('data-garment-lengths-data', garmentLengthsData.replace(/"/g, '&quot;'));
          console.log('[QuickAdd] Garment lengths data stored from variant picker');
          return;
        }
      }

      // Try to find in JSON-LD structured data
      const jsonLdScripts = html.querySelectorAll('script[type="application/ld+json"]');
      for (const script of jsonLdScripts) {
        try {
          const data = JSON.parse(script.textContent);
          if (data['@type'] === 'Product' && data.garmentLengths) {
            const garmentLengthsData = JSON.stringify(data.garmentLengths);
            garmentLengthsOutput.setAttribute('data-garment-lengths-data', garmentLengthsData.replace(/"/g, '&quot;'));
            console.log('[QuickAdd] Garment lengths data stored from JSON-LD');
            return;
          }
        } catch (e) {
          // Not valid JSON or not product data, continue
        }
      }
    } catch (error) {
      console.warn('[QuickAdd] Error extracting garment lengths from HTML:', error);
    }
  }

  /**
   * Extracts garment lengths from HTML document for a specific product
   * @param {Document} html - The HTML document
   * @param {string} productHandle - The product handle
   */
  async #extractGarmentLengthsFromHTMLForProduct(html, productHandle) {
    await this.#extractGarmentLengthsFromHTML(html, productHandle);
  }

  /**
   * Updates garment lengths display based on selected size
   * @param {string} sizeValue - The selected size value (e.g., "S", "M", "L")
   */
  #updateGarmentLengths(sizeValue) {
    const garmentLengthsOutput = document.querySelector('[data-garment-lengths-output]');
    if (!garmentLengthsOutput) {
      console.warn('[QuickAdd] Garment lengths output element not found when updating');
      return;
    }

    let garmentLengthsData = garmentLengthsOutput.getAttribute('data-garment-lengths-data');
    
    // Check if data exists and is not null or empty
    if (!garmentLengthsData || garmentLengthsData === 'null' || garmentLengthsData.trim() === '') {
      console.log('[QuickAdd] No garment lengths data found for size:', sizeValue);
      garmentLengthsOutput.style.display = 'none';
      return;
    }

    try {
      // Handle HTML entities
      let cleanData = garmentLengthsData.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
      cleanData = cleanData.trim();
      const garmentData = JSON.parse(cleanData);
      const sizeData = garmentData[sizeValue];
      
      console.log('[QuickAdd] Garment data for size', sizeValue, ':', sizeData);
      
      if (sizeData && typeof sizeData === 'object') {
        const measurements = [];
        for (const [key, value] of Object.entries(sizeData)) {
          measurements.push(`${key}: ${value}`);
        }
        
        const valueTarget = garmentLengthsOutput.querySelector('[data-garment-lengths-value]');
        if (valueTarget) {
          valueTarget.textContent = measurements.join(' | ');
          garmentLengthsOutput.style.display = 'block';
          console.log('[QuickAdd] Garment lengths displayed:', measurements.join(' | '));
        } else {
          console.warn('[QuickAdd] Garment lengths value element not found');
          garmentLengthsOutput.style.display = 'none';
        }
      } else {
        console.log('[QuickAdd] No data found for size:', sizeValue, 'in garment data');
        garmentLengthsOutput.style.display = 'none';
      }
    } catch (e) {
      console.error('[QuickAdd] Error parsing garment lengths data:', e);
      console.error('[QuickAdd] Raw data:', garmentLengthsData);
      console.error('[QuickAdd] Size value:', sizeValue);
      garmentLengthsOutput.style.display = 'none';
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /** Search modal can remain open underneath; close it whenever footer ATC is activated (capture = before stopPropagation). */
  #onFooterAtcClickCapture = (/** @type {Event} */ event) => {
    if (!(event.target instanceof Element)) return;
    const btn = event.target.closest('.quick-add-atc-button');
    if (!(btn instanceof HTMLElement)) return;
    if (!this.contains(btn)) return;
    closeThemeSearchDrawer();
  };

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#onFooterAtcClickCapture, { capture: true });

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleDocumentCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#onFooterAtcClickCapture, { capture: true });

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog when cart updates bubble from descendants (e.g. product-form in modal).
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  /**
   * Closes when quick-add dispatches cart:update on document (footer ATC API fallback).
   * @param {Event} event
   */
  #handleDocumentCartUpdate = (event) => {
    if (!(event instanceof CartUpdateEvent) && !(event instanceof CartAddEvent)) return;
    if (event.detail?.data?.didError) return;
    if (event.detail?.sourceId !== 'quick-add') return;
    if (!this.refs.dialog.open) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
